<script setup lang="ts">
import { sumBy } from 'lodash-es';
import { computed, onBeforeMount, ref } from 'vue';

import { getPropertyOwnershipHeader } from '@/helpers/builder';
import { BorrowerType, LoanType } from '@/helpers/const';
import { pendingOwnershipStorage } from '@/helpers/pendingOwnershipStorage';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { DraftEventName } from '@/helpers/draftEvents';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useCalcApi } from '@/helpers/useCalcApi';
import { BrokerProcessStep, StepStatus } from '@/types';

import BorrowerOwnershipForm from './modules/PropertyOwnership/BorrowerOwnershipForm.vue';

import type { PropertyOwnershipType } from '@/types/PropertyOwnership';
import type {
  AssetId,
  IncomingProperty,
  OutgoingProperty,
  PropertyId,
  PropertyOwnership,
  SupportingProperty,
} from '@/types';
import { BorrowerRole } from '@/types/BorrowerGroup';
import type { BorrowerGroup } from '@/types/BorrowerGroup';

const { send, stepper, loanApp, state, saveAndGoNext, goBack } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const isLoading = ref(false);
const autoAddInfosList = ref<Array<{ propertyId: string; borrowerGroupIds: string[] }>>([]);

// When borrowerGroups is empty (legacy flow), derive synthetic groups from applicants
const borrowerGroups = computed<BorrowerGroup[]>(() => {
  const groups = loanApp.value.borrowerGroups ?? [];
  if (groups.length > 0) return groups;
  return loanApp.value.applicants.map((applicant) => ({
    id: applicant.id,
    type: BorrowerType.INDIVIDUAL,
    applicants: [{ id: applicant.id, applicantId: applicant.id, role: BorrowerRole.INDIVIDUAL }],
  }));
});

// ============================================
// BORROWER OWNERSHIP UI
// ============================================

// Combined property type for unified handling
interface PropertyWithType {
  property: IncomingProperty | OutgoingProperty | SupportingProperty;
  type: PropertyOwnershipType;
}

// Get all properties including supporting properties for borrower ownership UI
const allPropertiesForBorrowerUI = computed<PropertyWithType[]>(() => {
  const result: PropertyWithType[] = [];

  // Add incoming properties (bridging only)
  if (loanApp.value.loanType === LoanType.BRIDGING) {
    for (const prop of loanApp.value.incomingProperties) {
      result.push({ property: prop, type: 'incoming' });
    }
  }

  for (const prop of loanApp.value.outgoingProperties) {
    result.push({ property: prop, type: 'outgoing' });
  }

  for (const prop of loanApp.value?.supportingProperties ?? []) {
    result.push({ property: prop, type: 'supporting' });
  }

  return result;
});

const allIndividualBorrowers = computed(
  () => borrowerGroups.value.length > 0 && borrowerGroups.value.every((g) => g.type === BorrowerType.INDIVIDUAL),
);

const getDefaultShareForIndividualBorrowers = (): Record<string, number> => {
  const groups = borrowerGroups.value;
  if (groups.length === 0) return {};
  if (groups.length === 1) return { [groups[0].id]: 100 };
  if (groups.length === 2) return { [groups[0].id]: 50, [groups[1].id]: 50 };
  const base = Math.floor(100 / groups.length);
  const remainder = 100 - base * groups.length;
  const result: Record<string, number> = {};
  groups.forEach((g, i) => {
    result[g.id] = base + (i < remainder ? 1 : 0);
  });
  return result;
};

const initializeBorrowerOwnership = (): PropertyOwnership[] => {
  // Create a mapping from applicantId to borrowerGroupId
  const applicantToBorrowerGroupMap: Record<string, string> = {};
  for (const group of borrowerGroups.value) {
    // Safely access applicants with optional chaining and provide fallback
    const applicants = group.applicants ?? [];
    for (const applicant of applicants) {
      if (applicant?.applicantId) {
        applicantToBorrowerGroupMap[applicant.applicantId] = group.id;
      }
    }
  }

  const validIds = new Set(borrowerGroups.value.map((g) => g.id));

  // Check if all properties have empty ownership (after converting to borrower group IDs)
  const allPropertiesEmpty = allPropertiesForBorrowerUI.value.every(({ property }) => {
    const existing = loanApp.value.propertiesOwnership?.find((o) => o.id === property.id);
    if (!existing) return true;

    // Convert any applicant IDs to borrower group IDs
    const convertedShare: Record<string, number> = {};
    for (const [key, value] of Object.entries(existing.share ?? {})) {
      // If the key is an applicant ID, convert to borrower group ID
      if (applicantToBorrowerGroupMap[key]) {
        convertedShare[applicantToBorrowerGroupMap[key]] = value;
      } else {
        convertedShare[key] = value;
      }
    }

    return Object.keys(convertedShare).length === 0;
  });

  const shouldAutoFill = allIndividualBorrowers.value && allPropertiesEmpty;
  const defaultShare = shouldAutoFill ? getDefaultShareForIndividualBorrowers() : {};

  // Create auto-add info for ALL properties
  const autoAddInfos: Array<{ propertyId: string; borrowerGroupIds: string[] }> = [];

  const result = allPropertiesForBorrowerUI.value.map(({ property, type }) => {
    const existing = loanApp.value.propertiesOwnership?.find((o) => o.id === property.id);

    // Convert any applicant IDs to borrower group IDs
    let share: Record<string, number> = {};
    if (existing) {
      for (const [key, value] of Object.entries(existing.share ?? {})) {
        // First check if it's an applicant ID that maps to a borrower group
        if (applicantToBorrowerGroupMap[key]) {
          share[applicantToBorrowerGroupMap[key]] = value;
        }
        // Then check if it's already a valid borrower group ID
        else if (validIds.has(key)) {
          share[key] = value;
        }
        // Otherwise, skip it (stale ID)
      }
    }

    // If we should auto-fill and this property has no ownership
    if (shouldAutoFill && Object.keys(share).length === 0) {
      share = { ...defaultShare };
      autoAddInfos.push({
        propertyId: property.id,
        borrowerGroupIds: Object.keys(defaultShare),
      });
    }

    return {
      id: property.id,
      type,
      share,
    };
  });

  autoAddInfosList.value = autoAddInfos;

  return result;
};

// Update getAutoAddBorrowerIds
const getAutoAddBorrowerIds = (propertyId: string): string[] => {
  if (autoAddInfo.value?.propertyId === propertyId) {
    return autoAddInfo.value.borrowerGroupIds;
  }
  const info = autoAddInfosList.value.find((item) => item.propertyId === propertyId);
  return info?.borrowerGroupIds ?? [];
};

const borrowerPropertyOwnership = ref<PropertyOwnership[]>(initializeBorrowerOwnership());
const autoAddInfo = ref<{ propertyId: string; borrowerGroupIds: string[] } | null>(null);

const handleAddBorrower = (propertyId: string) => {
  pendingOwnershipStorage.set({
    propertyId,
    existingBorrowerGroupIds: borrowerGroups.value.map((g) => g.id),
    propertyOwnershipState: borrowerPropertyOwnership.value,
  });
  goBack();
};

// Check for pending ownership addition during setup (before children mount)
const processPendingOwnershipAddition = () => {
  const pending = pendingOwnershipStorage.get();
  if (!pending) return;

  pendingOwnershipStorage.remove();

  // Restore ownership state if available (preserves user-entered percentages)
  if (pending.propertyOwnershipState?.length) {
    borrowerPropertyOwnership.value = pending.propertyOwnershipState;
  }

  const currentIds = borrowerGroups.value.map((g) => g.id);
  const newIds = currentIds.filter((id) => !pending.existingBorrowerGroupIds.includes(id));

  if (newIds.length === 0) return;

  autoAddInfo.value = {
    propertyId: pending.propertyId,
    borrowerGroupIds: newIds,
  };
};

// Run during setup so autoAddInfo is available before children render
processPendingOwnershipAddition();

const getPropertyIcon = (type: PropertyOwnershipType): 'incomingProperty' | 'outgoingProperty' | 'houseInfo' => {
  switch (type) {
    case 'incoming':
      return 'incomingProperty';
    case 'outgoing':
      return 'outgoingProperty';
    case 'supporting':
      return 'houseInfo';
    default:
      return 'outgoingProperty';
  }
};

const getPropertyHeader = (index: number): string => {
  const item = allPropertiesForBorrowerUI.value[index];
  if (!item) return 'Property';
  return getPropertyOwnershipHeader(item.type, item.property.address);
};

const showValidation = ref(false);

const getValidBorrowerGroupIds = (): Set<string> => new Set(borrowerGroups.value.map((g) => g.id));

// Calculate ownership total only for valid borrower group IDs (borrower flow)
// This filters out stale/invalid IDs that may exist in saved data
const getValidBorrowerOwnershipTotal = (share: Record<string, number>): number => {
  const validIds = getValidBorrowerGroupIds();
  return sumBy(
    Object.keys(share).filter((id) => validIds.has(id)),
    (id) => Number(share[id]) || 0,
  );
};

// Check if all properties have valid ownership
// Uses getValidBorrowerOwnershipTotal to ignore stale borrower group IDs
const validateBorrowerOwnership = () =>
  borrowerPropertyOwnership.value.every((p) => getValidBorrowerOwnershipTotal(p.share) === 100);

// Check specific property ownership for UI error display
// Uses getValidBorrowerOwnershipTotal to ignore stale borrower group IDs
const checkBorrowerOwnership = (propertyId: PropertyId | AssetId): boolean => {
  if (!showValidation.value) return true;
  const property = borrowerPropertyOwnership.value.find((p) => p.id === propertyId);
  return !property || getValidBorrowerOwnershipTotal(property.share) === 100;
};

const saveAndContinue = async () => {
  showValidation.value = true;

  if (!validateBorrowerOwnership()) return;

  isLoading.value = true;
  let applicationDraftId = loanApp.value.applicationDraftId;
  try {
    if (loanApp.value.brokerId) {
      applicationDraftId = await saveApplicationToDraft(
        state.value.context,
        {
          ...loanApp.value,
          propertiesOwnership: borrowerPropertyOwnership.value,
        },
        DraftEventName.PropertyOwnership,
      );
    }
    saveAndGoNext({
      propertiesOwnership: borrowerPropertyOwnership.value,
      applicationDraftId,
    });
  } catch (error) {
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
};

const isReady = ref(false);
const calc = useCalcApi();

// End debt is only recalculated here for bridging loans (commercial and residential alike) —
// the loan details step captures the full loan amount which is required by the formula. For
// single-security loans, PropertyDetailsStep already calculates and stores endDebt, and
// recalculating here risks overwriting it with 0.
const isBridging = computed(() => loanApp.value.loanType === LoanType.BRIDGING);

onBeforeMount(async () => {
  isReady.value = false;
  try {
    if (
      isBridging.value &&
      loanApp.value.loanDetails &&
      stepper.value.find((s) => s.id === BrokerProcessStep.PropertyOwnership)?.status === StepStatus.Ready
    ) {
      const endDebt = (await calc.getEndDebt(loanApp.value)) ?? 0;
      send({
        type: 'UPDATE',
        loanApp: { ...loanApp.value, endDebt },
      });
    }
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the
        <BxHighlight>ownership percentage</BxHighlight>
        for each property included in the application?
      </h2>
      <p class="mt-2 text-gray-600">All owners must be entered in the 'borrower details' section of the application.</p>
    </div>

    <template v-for="(property, propertyIndex) in borrowerPropertyOwnership" :key="property.id">
      <BxExpander class="mt-6" separator overflow-visible>
        <template #header>
          <div class="px-4 py-5 sm:px-6">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block">
                  <BxIcon :name="getPropertyIcon(property.type)" />
                </span>
                <span>{{ getPropertyHeader(propertyIndex) }}</span>
              </h3>
            </div>
            <div class="pb-3">
              <BorrowerOwnershipForm
                v-model="property.share"
                class="mt-4"
                :data-test-id="`borrower-ownership-form-${property.id}`"
                :borrower-groups="borrowerGroups"
                :applicants="loanApp.applicants"
                :error="!checkBorrowerOwnership(property.id)"
                :auto-add-borrower-ids="getAutoAddBorrowerIds(property.id)"
                @add-borrower="handleAddBorrower(property.id)"
              />
            </div>
            <div v-if="showValidation && !checkBorrowerOwnership(property.id)">
              <p class="text-rose-500">Ownership must total to 100% for this property.</p>
            </div>
          </div>
        </template>
      </BxExpander>
    </template>

    <div class="mt-6 flex gap-4">
      <BxButton
        id="property-ownership-continue"
        data-test-id="property-ownership-continue"
        :loading="!isReady || isLoading"
        variant="primary"
        @click="saveAndContinue()"
      >
        Save and continue
      </BxButton>
      <BxButton data-test-id="property-ownership-back" variant="link" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>
